import type { Language, ScanResponse } from "../../types";
import { pickScanFullRewriteText } from "../../utils/scanFullRewrite";

/** API 有时省略 flaggedItems；未规范化时写入会导致读库校验失败、列表永远为空。 */
export function normalizeScanResponse(result: ScanResponse): ScanResponse {
  const flaggedItems = Array.isArray(result.flaggedItems) ? result.flaggedItems : [];
  const fullDoc = pickScanFullRewriteText(result);
  const next = { ...result, flaggedItems } as ScanResponse & Record<string, unknown>;
  delete next.rewrite_text;
  delete next.rewriteText;
  delete next.fullRewriteText;
  if (fullDoc) {
    next.full_rewrite_text = fullDoc;
  }
  return next as ScanResponse;
}

export type StudioVerticalKey =
  | "cosmetics"
  | "pet_food"
  | "baby"
  | "supplements";

/** 浏览器 localStorage 一条扫描记录（与扫描记录表列对应）。 */
export type StoredStudioScan = {
  /** 扫描 ID，对应表列「扫描 ID」 */
  scanDisplayId: string;
  /** 完成时间戳，对应表列「时间」 */
  completedAt: number;
  /** 发起扫描时填写的「产品名称 / 内部名称」，对应表列「产品名称」 */
  internalName: string;
  /** 后端 review_tasks.id，接口返回 task_id 时写入，便于与服务器记录对齐 */
  taskId?: string;
  verticalKey: StudioVerticalKey;
  scanLang: Language;
  claimsText: string;
  /** 接口返回：合规分、摘要、flaggedItems、fda 等，对应得分与风险标签 */
  result: ScanResponse;
  /** 关联系列（版本对比）；与库表 `review_series.id` / 接口 `/api/studio/series` 一致 */
  seriesId?: string;
  /** 用户点击「定稿报告」并导出后的本地时间戳（毫秒） */
  finalizedAt?: number;
};

const KEY = "xtool_studio_scan_history";
const MAX = 50;

/** 按完成时间新→旧；时间相同则按扫描 ID 稳定排序 */
export function sortStudioScansByTime(rows: StoredStudioScan[]): StoredStudioScan[] {
  return [...rows].sort((a, b) => {
    const ta = Number.isFinite(a.completedAt) ? a.completedAt : 0;
    const tb = Number.isFinite(b.completedAt) ? b.completedAt : 0;
    if (tb !== ta) {
      return tb - ta;
    }
    return b.scanDisplayId.localeCompare(a.scanDisplayId);
  });
}

export function newScanDisplayId(): string {
  const s = Date.now()
    .toString(36)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  return `FDA-${s.slice(-6).padStart(6, "0")}`;
}

export function loadStudioScanHistory(): StoredStudioScan[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const rows = parsed
      .filter(isStoredStudioScanLoose)
      .map((row) => ({
        ...row,
        result: normalizeScanResponse(row.result),
      }));
    return sortStudioScansByTime(rows).slice(0, MAX);
  } catch {
    return [];
  }
}

export function getStudioScanById(
  scanDisplayId: string,
): StoredStudioScan | null {
  return (
    loadStudioScanHistory().find((r) => r.scanDisplayId === scanDisplayId) ??
    null
  );
}

export function appendStudioScan(record: StoredStudioScan): void {
  const normalized: StoredStudioScan = {
    ...record,
    result: normalizeScanResponse(record.result),
  };
  const prev = loadStudioScanHistory();
  const next = sortStudioScansByTime([
    normalized,
    ...prev.filter((r) => r.scanDisplayId !== normalized.scanDisplayId),
  ]).slice(0, MAX);
  localStorage.setItem(KEY, JSON.stringify(next));
}

/** 按 scanDisplayId 或 taskId 去重合并，用于服务端拉取后写入本机（定稿状态等）。 */
export function upsertStudioScan(record: StoredStudioScan): void {
  const normalized: StoredStudioScan = {
    ...record,
    result: normalizeScanResponse(record.result),
  };
  const prev = loadStudioScanHistory();
  const matches = (r: StoredStudioScan) =>
    r.scanDisplayId === normalized.scanDisplayId ||
    Boolean(normalized.taskId && r.taskId === normalized.taskId);
  const next = sortStudioScansByTime([
    normalized,
    ...prev.filter((r) => !matches(r)),
  ]).slice(0, MAX);
  localStorage.setItem(KEY, JSON.stringify(next));
}

function isStoredStudioScanLoose(x: unknown): x is StoredStudioScan {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  const res = o.result;
  if (res === null || typeof res !== "object") return false;
  const r = res as ScanResponse;
  const fi = r.flaggedItems;
  if (fi !== undefined && !Array.isArray(fi)) return false;
  if (o.taskId !== undefined && typeof o.taskId !== "string") return false;
  if (o.seriesId !== undefined && typeof o.seriesId !== "string") return false;
  if (o.finalizedAt !== undefined && typeof o.finalizedAt !== "number") {
    return false;
  }
  return (
    typeof o.scanDisplayId === "string" &&
    typeof o.completedAt === "number" &&
    typeof o.internalName === "string" &&
    typeof o.verticalKey === "string" &&
    (o.scanLang === "en" || o.scanLang === "zh") &&
    typeof o.claimsText === "string" &&
    typeof r.score === "number"
  );
}

/** 写入或移除某条扫描的系列关联；`seriesId` 为空则从记录中删除该字段 */
export function setScanSeriesId(
  scanDisplayId: string,
  seriesId: string | undefined,
): boolean {
  const prev = loadStudioScanHistory();
  const idx = prev.findIndex((r) => r.scanDisplayId === scanDisplayId);
  if (idx < 0) return false;
  const row = { ...prev[idx] };
  if (seriesId === undefined || seriesId === "") {
    delete row.seriesId;
  } else {
    row.seriesId = seriesId;
  }
  const next = sortStudioScansByTime([
    ...prev.filter((r) => r.scanDisplayId !== scanDisplayId),
    row,
  ]).slice(0, MAX);
  localStorage.setItem(KEY, JSON.stringify(next));
  return true;
}

/** 定稿：写入 `finalizedAt` 并持久化到 localStorage */
export function markScanFinalized(
  scanDisplayId: string,
  finalizedAt: number,
): boolean {
  const prev = loadStudioScanHistory();
  const idx = prev.findIndex((r) => r.scanDisplayId === scanDisplayId);
  if (idx < 0) return false;
  const row = { ...prev[idx], finalizedAt };
  const next = sortStudioScansByTime([
    ...prev.filter((r) => r.scanDisplayId !== scanDisplayId),
    row,
  ]).slice(0, MAX);
  localStorage.setItem(KEY, JSON.stringify(next));
  return true;
}
